"use client";

import React from "react";
import { Calendar, User, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

declare const wvcClient: any;

function MainPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 animate-pulse">
        <div className="h-12 bg-muted rounded-md w-3/4 mb-6"></div>
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-muted rounded-lg"></div>
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded-md w-24"></div>
            <div className="h-3 bg-muted rounded-md w-32"></div>
          </div>
        </div>
        <div className="h-64 bg-muted rounded-lg mb-8 w-full"></div>
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded-md w-full"></div>
          <div className="h-4 bg-muted rounded-md w-full"></div>
          <div className="h-4 bg-muted rounded-md w-5/6"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="max-w-3xl mx-auto px-4 text-center text-destructive py-12 font-default">Failed to load event details.</div>;
  }

  if (!posts || posts.length === 0) {
    return <div className="max-w-3xl mx-auto px-4 text-center text-muted-foreground py-12 font-default">Event story not found.</div>;
  }

  const post = posts[0];

  return (
    <article className="max-w-3xl mx-auto px-4">
      <header className="mb-10 text-center md:text-left">
        <h1 className="text-4xl md:text-5xl font-bold font-default mb-6 leading-tight text-primary">
          {post.title}
        </h1>
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-muted-foreground">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-border">
              <AvatarImage src={post.author?.avatar?.['48']} alt={post.author?.name || "Planner"} />
              <AvatarFallback className="bg-primary/10 text-primary font-default">
                {post.author?.name?.[0] || <User className="h-5 w-5" />}
              </AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground font-default">{post.author?.name || "Signature Planner"}</p>
              {post.date && (
                <div className="flex items-center text-xs gap-1 mt-0.5 font-serif italic">
                  <Calendar className="h-3 w-3" />
                  <span>{post.date}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto max-h-[600px] object-cover rounded-lg shadow-lg"
          />
        </figure>
      )}

      <div
        className="prose prose-lg dark:prose-invert max-w-none font-serif text-foreground/90 leading-relaxed mb-16"
        dangerouslySetInnerHTML={{ __html: post.content || "<p>No event details available.</p>" }}
      />

      <div className="my-16 p-8 md:p-12 bg-accent text-accent-foreground rounded-lg shadow-xl flex flex-col items-center text-center border border-primary/10">
        <Link to="/HomePage">
          <img src="/img/signature_occasions_logo.png" alt="Signature Occasions Logo" className="h-12 mb-6 object-contain" />
        </Link>
        <h2 className="text-3xl md:text-4xl font-bold font-default mb-4 tracking-tight text-primary">Signature Occasions</h2>
        <p className="text-lg md:text-xl mb-8 font-serif italic opacity-90">Ready to plan your dream celebration?</p>
        <Link to="/LetsTalkPage">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold font-default rounded-md py-6 px-8 text-lg shadow-md transition-transform hover:scale-105">
            BOOK A CONSULTATION
          </Button>
        </Link>
        <p className="mt-10 text-sm opacity-60 font-default tracking-wide">© 2024 Signature Occasions. Elegance in every detail.</p>
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 mt-20 pt-16 border-t border-border">
      <h3 className="text-2xl font-bold font-default mb-8 text-primary">More Inspirations</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {posts.slice(0, 3).map((post, i) => (
          <Card key={post.id} data-index={i} className="h-full flex flex-col overflow-hidden border-border/50 shadow-md hover:shadow-lg transition-shadow bg-card rounded-lg">
            {post.featuredImage && (
              <Link to={post.link || "#"} className="block overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-48 object-cover transition-transform hover:scale-105 duration-500"
                />
              </Link>
            )}
            <CardHeader className="pb-4">
              <CardTitle className="font-default text-xl line-clamp-2 leading-tight text-foreground">
                <Link to={post.link || "#"} className="hover:text-primary transition-colors">
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-grow pb-4">
              <p className="text-sm text-muted-foreground line-clamp-3 font-serif">
                {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "Discover more about our bespoke event planning services..."}
              </p>
            </CardContent>
            <CardFooter className="pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="ghost" className="w-full justify-between group font-default text-primary hover:bg-primary/5">
                  View Event
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && window.wvcClient ? window.wvcClient.getCurrentWPQueryParams() : { post_type: 'post' };
  const wp_query = new WP_Query(currentWPQueryParams);

  
  const related_query = new WP_Query({ post_type: 'post', per_page: 3 });

  return (
    <section data-section-id="239144" id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24 min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <MainPostConsumer />
      </PostsProvider>
      
      <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
        <RelatedPostsConsumer />
      </PostsProvider>
    </section>
  );
}